import { useEffect, useRef } from 'react';

interface UseWindowRefreshOptions {
  enabled?: boolean;
  intervalMs?: number;
  refreshOnFocus?: boolean;
  refreshOnVisible?: boolean;
}

export const useWindowRefresh = (
  refresh: () => void | Promise<void>,
  {
    enabled = true,
    intervalMs,
    refreshOnFocus = true,
    refreshOnVisible = true,
  }: UseWindowRefreshOptions = {},
) => {
  const refreshRef = useRef(refresh);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const runRefresh = () => {
      if (isRefreshingRef.current) {
        return;
      }

      isRefreshingRef.current = true;
      void Promise.resolve(refreshRef.current()).finally(() => {
        isRefreshingRef.current = false;
      });
    };

    const intervalHandle = intervalMs && intervalMs > 0
      ? window.setInterval(runRefresh, intervalMs)
      : undefined;

    const handleFocus = () => {
      if (refreshOnFocus) {
        runRefresh();
      }
    };

    const handleVisibilityChange = () => {
      if (refreshOnVisible && document.visibilityState === 'visible') {
        runRefresh();
      }
    };

    if (refreshOnFocus) {
      window.addEventListener('focus', handleFocus);
    }

    if (refreshOnVisible) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (intervalHandle !== undefined) {
        window.clearInterval(intervalHandle);
      }

      if (refreshOnFocus) {
        window.removeEventListener('focus', handleFocus);
      }

      if (refreshOnVisible) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [enabled, intervalMs, refreshOnFocus, refreshOnVisible]);
};
